import { OpenInNew } from '@mui/icons-material';
import { Avatar, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import cx from 'classnames';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { Link } from 'wouter';
import { signOut as signOutModern } from '~/signOut';
import { darkTheme, lightTheme } from '~/theme.css';
import { isBun, LEGACY, MODERN, transitionDuration } from '~/utils/constants';
import registerComponent from '~/utils/registerComponent';
import { useTheme } from '~/utils/store';
import { type Profile } from '~/utils/types';
import { capitalizeName, legacyClick } from '~/utils/utils';
import * as styles from './AvatarMenu.css';

/**
 * @param {Object} props
 * @param {Function} [props.signOut] - callback for legacy
 * @param {Function} [props.signIn] - callback for legacy
 **/
export const AvatarMenu = ({ user, signIn, signOut }: { user: Profile; signIn?: () => void; signOut?: () => void }) => {
  const menu = usePopupState({ variant: 'popover' });
  const theme = useTheme();
  const size = LEGACY ? 40 : 36;
  return (
    <>
      <Tooltip
        enterDelay={isBun ? 0 : 500}
        enterNextDelay={500}
        slotProps={{
          tooltip: {
            style: {
              margin: 0,
            },
          },
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 4],
                },
              },
            ],
          },
        }}
        title={
          <div
            style={{
              fontSize: '1.2em',
              padding: '.4em',
              fontFamily: 'Lato, Arial',
            }}
          >
            <strong>{capitalizeName(user.name)}</strong>
            <div style={{ marginTop: 3 }}>{user.email}</div>
          </div>
        }
      >
        <IconButton disableTouchRipple style={{ margin: -8 }} {...bindTrigger(menu)}>
          <Avatar
            sx={{ width: size, height: size }}
            className={styles.avatar}
            src={user.image}
            alt={user.name}
            slotProps={{
              img: {
                referrerPolicy: 'no-referrer',
              },
            }}
          />
        </IconButton>
      </Tooltip>
      <Menu
        {...bindMenu(menu)}
        elevation={6}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transitionDuration={transitionDuration}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className={cx(styles.menu, {
          [darkTheme]: theme === 'dark',
          [lightTheme]: theme === 'light',
        })}
        slotProps={{
          paper: {
            style: {
              marginLeft: -8,
              marginTop: 2,
            },
          },
        }}
      >
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          to="/app/invite"
          component={Link}
          onClick={legacyClick}
        >
          Invite
        </MenuItem>

        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          to="https://docs.youneedawiki.com"
          target="_blank"
          component={Link}
          onClick={menu.close}
        >
          Docs <OpenInNew style={{ fontSize: 16, color: '#8f8f8f', alignSelf: 'center', marginLeft: 8 }} />
        </MenuItem>
        {user.stripe && (
          <MenuItem
            className={styles.menuItem}
            focusVisibleClassName={styles.menuItemFocusVisible}
            to={`https://billing.stripe.com/p/login/3cs5n86dL3sKeaI000?prefilled_email=${user.email}`}
            target="_blank"
            component={Link}
            onClick={menu.close}
          >
            Billing <OpenInNew style={{ fontSize: 16, color: '#8f8f8f', alignSelf: 'center', marginLeft: 8 }} />
          </MenuItem>
        )}
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          to="https://feedback.youneedawiki.com"
          target="_blank"
          component={Link}
          onClick={menu.close}
        >
          Feedback <OpenInNew style={{ fontSize: 16, color: '#8f8f8f', alignSelf: 'center', marginLeft: 8 }} />
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          to="/app/support"
          component={Link}
          onClick={legacyClick}
        >
          Support
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => {
            TrackJS.track('User reported error');
            menu.close();
            alert('Error report sent to youneedawiki.com');
          }}
        >
          Send error report
        </MenuItem>
        {!user && (
          <MenuItem
            className={styles.menuItem}
            focusVisibleClassName={styles.menuItemFocusVisible}
            to="/app"
            component={Link}
            onClick={signIn}
          >
            Sign in
          </MenuItem>
        )}
        {user && LEGACY && (
          <MenuItem
            className={styles.menuItem}
            focusVisibleClassName={styles.menuItemFocusVisible}
            to=""
            component={Link}
            onClick={signOut}
          >
            Sign out
          </MenuItem>
        )}
        {user && MODERN && (
          <MenuItem
            className={styles.menuItem}
            focusVisibleClassName={styles.menuItemFocusVisible}
            href="/api/user/signout"
            onClick={signOutModern}
            component="a"
          >
            Sign out
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

if (LEGACY) {
  registerComponent(AvatarMenu, 'wc-avatar-menu', {
    user: 'json',
    signIn: 'function',
    signOut: 'function',
  });
}
